"use client"

import { useState } from "react"
import { useRouter } from "next/navigation"
import Link from "next/link"
import { authClient } from "@/lib/auth-client"

export default function RegisterPage() {
  const router = useRouter()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)
    setLoading(true)

    const form = e.currentTarget
    const name = (form.elements.namedItem("name") as HTMLInputElement).value
    const email = (form.elements.namedItem("email") as HTMLInputElement).value
    const password = (form.elements.namedItem("password") as HTMLInputElement).value

    const companyName = (form.elements.namedItem("companyName") as HTMLInputElement).value.trim()
    const website = (form.elements.namedItem("website") as HTMLInputElement).value.trim()
    const phone = (form.elements.namedItem("phone") as HTMLInputElement).value.trim()

    const result = await authClient.signUp.email({ email, password, name })

    if (result.error) {
      setError(result.error.message ?? "Не вдалося створити акаунт. Спробуйте ще раз.")
      setLoading(false)
      return
    }

    // Fire-and-forget: save extra profile fields + trigger website auto-browse
    if (companyName || website || phone) {
      fetch("/api/onboarding/profile", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ companyName, website, phone }),
      }).catch(() => {})
    }

    router.push("/dashboard/onboarding")
  }

  return (
    <div className="rounded-2xl border border-border bg-card shadow-xl shadow-black/5 dark:shadow-black/30 p-8">
      <h1 className="text-2xl font-semibold text-foreground mb-1">Створення акаунту</h1>
      <p className="text-sm text-muted-foreground mb-6">AI Директор допоможе створити вашу команду агентів</p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="name" className="block text-xs font-medium text-foreground mb-1.5">
            Ім&apos;я
          </label>
          <input
            id="name"
            name="name"
            type="text"
            required
            autoComplete="name"
            placeholder="Ваше ім'я"
            className="w-full px-4 py-2.5 rounded-lg bg-background border border-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition"
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-xs font-medium text-foreground mb-1.5">
            Email
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            autoComplete="email"
            placeholder="you@example.com"
            className="w-full px-4 py-2.5 rounded-lg bg-background border border-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition"
          />
        </div>

        <div>
          <label htmlFor="password" className="block text-xs font-medium text-foreground mb-1.5">
            Пароль
          </label>
          <input
            id="password"
            name="password"
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
            placeholder="Мінімум 8 символів"
            className="w-full px-4 py-2.5 rounded-lg bg-background border border-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition"
          />
        </div>

        <div>
          <label htmlFor="companyName" className="block text-xs font-medium text-foreground mb-1.5">
            Назва компанії
          </label>
          <input
            id="companyName"
            name="companyName"
            type="text"
            autoComplete="organization"
            placeholder="Назва вашого бізнесу"
            className="w-full px-4 py-2.5 rounded-lg bg-background border border-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition"
          />
        </div>

        <div>
          <label htmlFor="website" className="block text-xs font-medium text-foreground mb-1.5">
            Вебсайт
          </label>
          <input
            id="website"
            name="website"
            type="url"
            autoComplete="url"
            placeholder="https://example.com"
            className="w-full px-4 py-2.5 rounded-lg bg-background border border-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition"
          />
        </div>

        <div>
          <label htmlFor="phone" className="block text-xs font-medium text-foreground mb-1.5">
            Телефон
          </label>
          <input
            id="phone"
            name="phone"
            type="tel"
            autoComplete="tel"
            placeholder="+380..."
            className="w-full px-4 py-2.5 rounded-lg bg-background border border-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition"
          />
        </div>

        {error && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full py-2.5 rounded-lg bg-primary text-primary-foreground font-medium shadow-md shadow-primary/20 hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          {loading ? "Створюю..." : "Створити акаунт"}
        </button>
      </form>

      <p className="mt-6 text-center text-sm text-muted-foreground">
        Вже маєте акаунт?{" "}
        <Link href="/login" className="text-primary hover:text-primary/80 font-medium transition">
          Увійти
        </Link>
      </p>
    </div>
  )
}
